/**
 * LoadingScreen — ekran ładowania na tle splashu z logiem gry (sesja 34B).
 *
 * Pokazywany w App.tsx w dwóch fazach, ZAWSZE na `SplashBackground`
 * (`title_splash.png` + meadow tint), żeby gracz widział logo + capibarę:
 *
 *  1. **Ładowanie save z serwera** (`!saveLoaded`) — spinner + tekst, bez paska
 *     (postępu pobierania nie znamy, to pojedynczy request).
 *  2. **Ładowanie grafik gry** (`progress` podany) — spinner + pasek postępu
 *     zasilany z PreloadScene (Phaser). Zastąpił surowy zielony ekran Phasera
 *     z osobnym paskiem — teraz pasek jest TU, na ekranie z logiem i spinnerem.
 *
 * Min display time (1.5 s) dla fazy 1 jest enforce'owany w App.tsx.
 */
interface LoadingScreenProps {
  /**
   * Postęp ładowania grafik ∈ [0, 1]. Podany → renderuje pasek postępu pod
   * spinnerem. `undefined` → faza „pobieram save z serwera" (bez paska).
   */
  progress?: number;
  /** Główny napis (domyślnie „Ładowanie gry…"). */
  label?: string;
  /** Podtytuł pod napisem. */
  subtitle?: string;
}

export function LoadingScreen({
  progress,
  label = 'Ładowanie gry…',
  subtitle = 'Pobieram Twój postęp z serwera',
}: LoadingScreenProps = {}): JSX.Element {
  const showBar = progress !== undefined;
  // Klamruj do [0,1] i zamień na procent (zaokrąglony) dla etykiety + szerokości.
  const pct = showBar ? Math.round(Math.min(1, Math.max(0, progress)) * 100) : 0;

  return (
    // Portrait: karta w dolnym pasie (`justify-end + pb-12`) żeby nie
    // zasłaniała planszy z napisem CAPIBARA + capibarą — splash na portrait
    // jest squeezed do paska w środku przez `object-contain`, więc karta na
    // środku zjadałaby logo. Landscape: vertical center (splash full-bleed,
    // karta na środku jest OK).
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-end pb-12 landscape:justify-center landscape:pb-0 font-game pointer-events-auto">
      <div className="bg-white/90 backdrop-blur-sm rounded-3xl px-8 py-6 shadow-2xl flex flex-col items-center">
        <div className="w-14 h-14 rounded-full border-4 border-meadow-300 border-t-meadow-600 animate-spin" />
        <p className="mt-5 text-lg font-bold text-meadow-700">{label}</p>
        <p className="mt-1 text-sm text-meadow-600/80">{subtitle}</p>

        {showBar && (
          <div className="mt-5 w-56 max-w-[70vw]">
            {/* Tor paska — zaokrąglony, jasny; wypełnienie animowane width'em
                zasilanym z postępu PreloadScene (Phaser). */}
            <div className="h-3 w-full rounded-full bg-meadow-200 overflow-hidden">
              <div
                className="h-full rounded-full bg-meadow-600 transition-[width] duration-200 ease-out"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="mt-2 text-center text-xs font-bold text-meadow-700 tabular-nums">{pct}%</p>
          </div>
        )}
      </div>
    </div>
  );
}
